/**
 * Capital, margin, and funds math for the Paper Trading Terminal.
 *
 * AUTHORITATIVE SEMANTICS:
 * 1. Opening Balance = capital as of the start of today, i.e. startCapital + (allTimeRealizedNet - todayRealizedNet).
 *    Today's activity never mutates opening balance.
 * 2. Available Margin = openingBalance - peakUsedMarginToday + min(netPnlToday, 0).
 *    Real broker rule: profit stays unrealized on cash ledger until T+1 and does not inflate
 *    intraday purchasing power, but intraday losses immediately reduce available margin.
 * 3. Take Home Breakdown: aggregates gross P&L, per-trade statutory charges, and net P&L.
 */
import { tradeNetPnl } from "../charges";

export interface ChargeBreakdownLike {
  stt?: number;
  stampDuty?: number;
  exchangeCharges?: number;
  sebiCharges?: number;
  miscCharges?: number;
  brokerage?: number;
  gst?: number;
  total?: number;
}

export interface TradeHistoryLike {
  pnl?: number | null;
  netPnl?: number | null;
  charges?: ChargeBreakdownLike | null;
}

export interface TakeHomeTotals {
  gross: number;
  stt: number;
  stamp: number;
  exchange: number;
  sebi: number;
  misc: number;
  brokerage: number;
  gst: number;
  totalCharges: number;
  net: number;
}

/**
 * Capital at the start of the current day.
 */
export function computeOpeningBalance(
  startCapital: number,
  allTimeRealizedNet: number,
  todayRealizedNet: number,
): number {
  return (startCapital || 0) + ((allTimeRealizedNet || 0) - (todayRealizedNet || 0));
}

/**
 * Purchasing power available to open new orders.
 */
export function computeAvailableMargin(
  openingBalance: number,
  peakUsedMargin: number,
  netPnlToday: number,
): number {
  const openBal = openingBalance || 0;
  const used = Math.max(0, peakUsedMargin || 0);
  const netPnl = netPnlToday || 0;
  // Losses reduce purchasing power immediately; profits do not add to intraday margin until T+1
  return openBal - used + Math.min(netPnl, 0);
}

/**
 * Top MTM gross calculation: gross realized today + current unrealized floating P&L.
 */
export function computeMtmGross(
  todayGrossRealized: number,
  unrealizedPnl: number,
): number {
  return (todayGrossRealized || 0) + (unrealizedPnl || 0);
}

/**
 * Aggregates all closed trade charges and net P&L for the Take Home report tab.
 */
export function rollupTakeHomeCharges(historyRows: TradeHistoryLike[]): TakeHomeTotals {
  const result: TakeHomeTotals = {
    gross: 0,
    stt: 0,
    stamp: 0,
    exchange: 0,
    sebi: 0,
    misc: 0,
    brokerage: 0,
    gst: 0,
    totalCharges: 0,
    net: 0,
  };

  if (!Array.isArray(historyRows)) return result;

  for (const h of historyRows) {
    if (!h) continue;
    const gross = h.pnl != null && !isNaN(h.pnl) ? h.pnl : 0;
    const c = h.charges || {};
    const stt = c.stt != null && !isNaN(c.stt) ? c.stt : 0;
    const stamp = c.stampDuty != null && !isNaN(c.stampDuty) ? c.stampDuty : 0;
    const exchange = c.exchangeCharges != null && !isNaN(c.exchangeCharges) ? c.exchangeCharges : 0;
    const sebi = c.sebiCharges != null && !isNaN(c.sebiCharges) ? c.sebiCharges : 0;
    const misc = c.miscCharges != null && !isNaN(c.miscCharges) ? c.miscCharges : 0;
    const brokerage = c.brokerage != null && !isNaN(c.brokerage) ? c.brokerage : 0;
    const gst = c.gst != null && !isNaN(c.gst) ? c.gst : 0;
    const totalCharges = c.total != null && !isNaN(c.total)
      ? c.total
      : stt + stamp + exchange + sebi + misc + brokerage + gst;
    const net = tradeNetPnl(h);

    result.gross += gross;
    result.stt += stt;
    result.stamp += stamp;
    result.exchange += exchange;
    result.sebi += sebi;
    result.misc += misc;
    result.brokerage += brokerage;
    result.gst += gst;
    result.totalCharges += totalCharges;
    result.net += net;
  }

  return result;
}
